/**
 * @file useStoreOperation.ts - Store Operation Handler Composable
 * @description
 * A Vue composable that provides a standardized way to handle store operations
 * with built-in error handling and loading states. Designed to work with
 * store-specific errors and provide user-friendly error messages.
 *
 * Features:
 * - Loading state management
 * - Typed error handling with StoreError
 * - Success and error callbacks
 * - User-friendly error messages
 * - Consistent error handling across stores
 *
 * @example
 * ```typescript
 * const { handleOperation, error, isLoading } = useStoreOperation();
 * 
 * // Using the handler
 * await handleOperation(
 *   async () => await store.saveData(),
 *   {
 *     onSuccess: (result) => console.log('Saved:', result),
 *     onError: (error) => console.error('Failed:', error),
 *     errorMessage: 'Custom error message'
 *   }
 * );
 * ```
 */

import { ref } from 'vue';
import { StoreError } from '@/utils/storeErrorHandling';
import { StoreErrorType } from '@/utils/storeErrorHandling';

export function useStoreOperation() {
  /**
   * Stores the current operation error
   * @type {Ref<StoreError | null>}
   */
  const error = ref<StoreError | null>(null);

  /**
   * Tracks the loading state during operations
   * @type {Ref<boolean>}
   */
  const isLoading = ref(false);

  /**
   * Handles store operations with consistent error handling and loading states
   * @template T - The type of the operation result
   * @param {() => Promise<T>} operation - The async operation to execute
   * @param {Object} options - Operation options
   * @param {(result: T) => void} [options.onSuccess] - Success callback
   * @param {(error: StoreError) => void} [options.onError] - Error callback
   * @param {string} [options.errorMessage] - Custom error message
   * @param {boolean} [options.throwError] - Whether to throw the error
   * @returns {Promise<T>} The result of the operation
   * @throws {StoreError} Rethrows the caught error after handling
   */
  const handleOperation = async <T>(
    operation: () => Promise<T>,
    options: {
      onSuccess?: (result: T) => void;
      onError?: (error: StoreError) => void;
      errorMessage?: string;
      throwError?: boolean;
    } = {}
  ) => {
    error.value = null;
    isLoading.value = true;

    try {
      const result = await operation();
      options.onSuccess?.(result);
      return result;
    } catch (e) {
      const storeError = e instanceof StoreError 
        ? e 
        : new StoreError(
            options.errorMessage || String(e),
            StoreErrorType.UNKNOWN,
            'UNKNOWN'
          );
      
      error.value = storeError;
      options.onError?.(storeError);

      if (options.throwError !== false) {
        throw storeError;
      }
      
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Converts store errors into user-friendly messages
   * @param {StoreError} error - The store error to convert
   * @returns {string} A user-friendly error message
   */
  const getErrorMessage = (error: StoreError): string => {
    switch (error.type) {
      case StoreErrorType.NETWORK:
        return 'Network error occurred. Please check your connection.';
      case StoreErrorType.VALIDATION:
        return 'Please check your input and try again.';
      default:
        return error.message;
    }
  };

  return {
    error,
    isLoading,
    handleOperation,
    getErrorMessage
  };
} 